import { useNavigate } from 'react-router-dom';
import { addAttributionToUrl } from '@assetron/common/utils/attributionUtils';
import { buildAuthSignInUrl } from '@assetron/common/utils/authUrl';
import useConfig from '@assetron/common/hooks/useConfig';
import { useLocalStorage } from '@uidotdev/usehooks';
import { RETURN_URL_QUERY_PARAM } from '@assetron/common/constants/constant';
import { AUTH_APP_RETURN_URL_QUERY } from '../constants/LocalStorageConstants';
import { QueryParamPersistence } from '../utils/queryParamPersistence';
import {
  appendBillingFlowDiagnosticsToUrl,
  billingFlowLog,
} from '../utils/billingFlowDiagnostics';
import { appendCreateOrganizationFlowQuery } from '../utils/planSelectionFlow';

type NavigateToMainAppOptions = {
  queryParams?: Record<string, string>;
};

export function AuthNavigation() {
  const navigate = useNavigate();
  const { AUTH_URL, APP_URL } = useConfig();
  const [authAppReturnUrl] = useLocalStorage(
    AUTH_APP_RETURN_URL_QUERY,
    APP_URL,
  );

  const isLocalHost = (value: string): boolean => {
    try {
      const parsed = new URL(value);
      return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    } catch {
      return false;
    }
  };

  const resolveLocalSafeReturnUrl = (): string => {
    const currentOrigin = globalThis.location.origin;
    const currentIsLocal = isLocalHost(currentOrigin);

    if (!currentIsLocal) {
      return authAppReturnUrl || APP_URL;
    }

    if (authAppReturnUrl && isLocalHost(authAppReturnUrl)) {
      return authAppReturnUrl;
    }

    if (APP_URL && isLocalHost(APP_URL)) {
      return APP_URL;
    }

    return currentOrigin;
  };

  const navigateToSignInLanding = () => navigate('/sign-in');
  const navigateToSignUpLanding = () =>
    navigate('/sign-up', { state: { step: 'landing' } });
  const navigateToSignUpDetails = () =>
    navigate('/sign-up', { state: { step: 'details' } });
  const navigateToSignUpEmailVerification = () =>
    navigate('/sign-up', { state: { step: 'emailVerification' } });
  const navigateToSignUpCreatePassword = () =>
    navigate('/sign-up', { state: { step: 'createPassword' } });
  const navigateToResetPassword = () =>
    navigate('/reset-password', { state: { step: 'enterEmail' } });
  const navigateToResetPasswordEmailVerification = () =>
    navigate('/reset-password', { state: { step: 'emailVerification' } });
  const navigateToResetPasswordCreatePassword = () =>
    navigate('/reset-password', { state: { step: 'createPassword' } });
  const navigateToResetPasswordSuccess = () =>
    navigate('/reset-password', { state: { step: 'success' } });
  const navigateToMainApp = (options?: NavigateToMainAppOptions) => {
    // Extract attribution data BEFORE clearing storage
    // This allows UTM parameters to be passed through the URL redirect
    // so they can be re-captured and stored in the new domain's localStorage

    const urlParams = new URLSearchParams(globalThis.location.search);
    const returnUrlFromParams = urlParams.get(RETURN_URL_QUERY_PARAM);

    // Start with explicit returnUrl when provided, otherwise use the default safe URL.
    let urlToNavigate = returnUrlFromParams || resolveLocalSafeReturnUrl();

    // First, append any persisted query parameters from the current page
    // (these may include UTM parameters from the original page load)
    if (QueryParamPersistence.hasPersistedQueryParams()) {
      urlToNavigate =
        QueryParamPersistence.getReturnUrlWithQueryParams(urlToNavigate);
    }

    if (options?.queryParams) {
      try {
        const target = new URL(urlToNavigate);

        Object.entries(options.queryParams).forEach(([key, value]) => {
          target.searchParams.set(key, value);
        });

        urlToNavigate = target.toString();
      } catch {
        // Keep the resolved return URL when it cannot be parsed.
      }
    }

    // Then, add attribution from localStorage, but skip UTM params
    // that already exist in the URL to avoid duplication
    urlToNavigate = addAttributionToUrl(urlToNavigate, true);

    // Clear storage after extracting attribution data
    globalThis.sessionStorage.clear();
    globalThis.localStorage.clear();

    // Redirect to main app with attribution in URL
    globalThis.location.href = urlToNavigate;
  };

  const navigateToOrganizationOnboarding = () => {
    const baseUrl = resolveLocalSafeReturnUrl();
    let urlToNavigate = baseUrl;

    try {
      const target = new URL(baseUrl);
      target.pathname = '/post-signup/passkey';
      target.search = '';
      target.hash = '';
      urlToNavigate = appendCreateOrganizationFlowQuery(target.toString());
    } catch {
      // Keep fallback base URL when URL parsing fails.
    }

    if (QueryParamPersistence.hasPersistedQueryParams()) {
      urlToNavigate =
        QueryParamPersistence.getReturnUrlWithQueryParams(urlToNavigate);
    }

    urlToNavigate = addAttributionToUrl(urlToNavigate, true);
    urlToNavigate = appendBillingFlowDiagnosticsToUrl(urlToNavigate);

    billingFlowLog('AuthNavigation', 'redirect_to_organization_onboarding', {
      destination: urlToNavigate,
    });

    globalThis.sessionStorage.clear();
    globalThis.localStorage.clear();
    globalThis.location.href = urlToNavigate;
  };

  const navigateToAuthApp = ({ queryParam }: { queryParam?: string }) => {
    const authUrl = AUTH_URL;
    const appUrl = APP_URL;
    globalThis.sessionStorage.clear();
    globalThis.localStorage.clear();
    if (authUrl && appUrl) {
      if (queryParam) {
        globalThis.location.replace(buildAuthSignInUrl(authUrl, queryParam));
      } else {
        const searchParams = new URLSearchParams();
        searchParams.set(RETURN_URL_QUERY_PARAM, appUrl);
        globalThis.location.replace(buildAuthSignInUrl(authUrl, searchParams));
      }
    }
  };

  return {
    navigateToSignUpLanding,
    navigateToSignInLanding,
    navigateToSignUpDetails,
    navigateToSignUpEmailVerification,
    navigateToSignUpCreatePassword,
    navigateToResetPassword,
    navigateToResetPasswordEmailVerification,
    navigateToResetPasswordCreatePassword,
    navigateToResetPasswordSuccess,
    navigateToMainApp,
    navigateToOrganizationOnboarding,
    navigateToAuthApp,
  };
}
